import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EndPointApi } from '@helpers/endpointapi';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { CustomGraph } from 'ops/common/models/custom-graph';
import { ChannelService } from './channel.service';

@Injectable({
    providedIn: 'root',
})
export class CustomGraphService {
    private domain: string;
    private headers: any = new HttpHeaders({
        'Content-Type': 'application/json',
    });

    private listSubject = new BehaviorSubject<CustomGraph[]>([]);
    public list$ = this.listSubject.asObservable();

    constructor(
        private http: HttpClient,
        private channelService: ChannelService,
    ) {
        this.domain = EndPointApi.iApiDomain();
    }

    public setList(list: CustomGraph[]) {
        this.listSubject.next(list);
    }

    public getListValue() {
        return this.listSubject.getValue();
    }

    public async getSelectedGraph(graphIdOrGraph: string) {
        if (!graphIdOrGraph) {
            return null;
        }
        const customGraphList = this.listSubject.getValue();
        let selectedCustomGraph = customGraphList.find(
            (customGraph) => customGraph.id === graphIdOrGraph,
        );

        if (!selectedCustomGraph) {
            return null;
        }

        let newGraphList = await Promise.all(
            selectedCustomGraph?.graphList?.map(async (graph) => {
                if (graph && graph.channel) {
                    const channel = await lastValueFrom(this.channelService.get(graph.channel.id));
                    return {
                        ...graph,
                        channel,
                    };
                } else {
                    return null;
                }
            }),
        );

        selectedCustomGraph = {
            ...selectedCustomGraph,
            graphList: newGraphList,
        };

        return selectedCustomGraph;
    }

    async getList(): Promise<CustomGraph[]> {
        try {
            const list = await lastValueFrom(
                this.http.get<CustomGraph[]>(`${this.domain}/api/customGraph`),
            );
            this.setList(list);
            return list;
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    async create(name: string, graphList: any[], publicAccess: boolean): Promise<any> {
        const newCustomGraph = await lastValueFrom(
            this.http.post<CustomGraph>(`${this.domain}/api/customGraph`, {
                headers: this.headers,
                params: {
                    name,
                    graphList,
                    publicAccess,
                },
            }),
        );
        return newCustomGraph;
    }

    async delete(graphId) {
        try {
            await lastValueFrom(
                this.http.delete(`${this.domain}/api/customGraph/${graphId}`, {
                    responseType: 'text',
                }),
            );
        } catch (error) {
            console.log(error);
        }
    }

    async update(graphId, data) {
        try {
            await lastValueFrom(
                this.http.put(`${this.domain}/api/customGraph/${graphId}`, {
                    headers: this.headers,
                    params: data,
                }),
            );
        } catch (error) {
            console.log(error);
        }
    }
}
